import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { useFileSystemDuplexer } from './use-file-system-duplexer';
import { queryClient, queryKeys } from './query-client';

// Client data layer for the empty-chat suggested-task chips. One fetch to /api/suggested-tasks (the
// symphony-bff ListSuggestedTasks projection of the genome's `_suggested-tasks.json`). orion-web's
// CLIENT bundle must not import common-lib, so the shape lives here as a client-local contract
// matching what the route returns.

export interface SuggestedTask {
  /** The chip's visible text. Short and imperative; already localized by the genome. */
  label: string;
  /** What to put in the COMPOSER on tap. The user still presses send — never auto-send this. */
  prompt: string;
  /** Why this business specifically. Shown on hover; empty means show no tooltip. */
  why: string;
  /** Composio toolkit slug for the chip's logo, or '' when the task rides no particular app. */
  connector: string;
  /** True when `connector` is NOT connected yet — the chip must say so before it is tapped. */
  requiresConnect: boolean;
}

export type SuggestedTasksState =
  | { status: 'loading' }
  | { status: 'ready'; tasks: SuggestedTask[] }
  /** Nothing to show. Deliberately distinct from 'ready' with an empty list only in intent: both
   *  render nothing, and neither is worth an error message under a working composer. */
  | { status: 'empty' };

export async function fetchSuggestedTasks(): Promise<SuggestedTask[]> {
  const res = await apiFetch(`${API_BASE}/api/suggested-tasks`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { tasks?: Partial<SuggestedTask>[] };
  return (data.tasks ?? [])
    .map((t) => ({
      label: t.label ?? '',
      // A chip whose tap puts nothing in the composer is a dead control.
      prompt: t.prompt || t.label || '',
      why: t.why ?? '',
      connector: t.connector ?? '',
      requiresConnect: Boolean(t.requiresConnect),
    }))
    .filter((t) => t.label.length > 0);
}

/** How many chips the row shows at once — the genome can author more than fit the composer. */
export const SUGGESTED_TASK_CHIP_COUNT = 3;

/**
 * `count` tasks picked at random out of the full authored list, in random order. Pure (takes the
 * randomness source as a parameter) so it is exercised without mocking `Math.random`. A list no
 * longer than `count` is returned as-is — there's nothing to sample down.
 */
export function pickRandomSuggestedTasks(tasks: SuggestedTask[], count = SUGGESTED_TASK_CHIP_COUNT, random: () => number = Math.random): SuggestedTask[] {
  if (tasks.length <= count) return tasks;
  // Fisher–Yates, stopped after `count` swaps — no need to shuffle the tail we're about to drop.
  const pool = [...tasks];
  for (let i = 0; i < count; i++) {
    const j = i + Math.floor(random() * (pool.length - i));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
}

/**
 * The chips for an empty chat.
 *
 * A failure here is NOT an error state: the composer under these chips is the actual feature, and a
 * red line where six optional buttons would be is worse than the buttons never appearing. So every
 * non-ready outcome — still loading, failed, none authored — resolves to a row that draws nothing.
 * The duplexer refresh matters because the genome rewrites this file on every recalc, and a user who
 * just finished onboarding should see their first chips without a reload.
 *
 * The genome can author more suggestions than the row should show at once, so only
 * `SUGGESTED_TASK_CHIP_COUNT` of them render — a random sample, re-picked whenever the underlying list
 * changes (a fresh fetch, a genome recalc via the duplexer), so the same handful doesn't calcify as
 * "the chips". The pick is memoized on the query's own data reference, not re-rolled on every render,
 * so it stays put while the chips are actually on screen (typing in the composer must not shuffle them
 * out from under a reader mid-decision).
 */
export function useSuggestedTasks(enabled: boolean): SuggestedTasksState {
  const query = useQuery({
    queryKey: queryKeys.suggestedTasks,
    queryFn: fetchSuggestedTasks,
    enabled,
  });

  useFileSystemDuplexer(() => {
    if (enabled) void queryClient.invalidateQueries({ queryKey: queryKeys.suggestedTasks });
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the data array's own identity
  const shown = useMemo(() => (query.data ? pickRandomSuggestedTasks(query.data) : undefined), [query.data]);

  if (shown) return shown.length > 0 ? { status: 'ready', tasks: shown } : { status: 'empty' };
  if (query.status === 'error') return { status: 'empty' };
  return enabled ? { status: 'loading' } : { status: 'empty' };
}
